/**
 * The header strip of a quote card: carrier logo, service level, earliest
 * delivery, transit days and price.
 *
 * Lives in CustomComponents/ because it is custom styling end to end and none
 * of it is expressible as ui-core props:
 *  - every column responds to the card's *measured* width through the
 *    `in-[.quote-card-medium|narrow|tiny]` ancestor variants, which restyle
 *    type sizes and switch the row into a two-column then single-column grid.
 *    Ancestor-conditional variants have no prop equivalent.
 *  - the columns are fixed pixel geometry (206/170/140/220px, and the narrower
 *    figures per card size) so every row's columns land at the same x.
 *  - the price block stacks a raised currency symbol, a 28px amount and a
 *    dropped currency label using sub-pixel `relative`/`-top-[3px]` offsets.
 */
import { LogoBackdrop } from "@freightsimple/ui-core/Display/LogoBackdrop";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Tooltip } from "@freightsimple/ui-core/Feedback/Tooltip";
import { displayNameForCarrier } from "@/Data/displayNameForCarrier";
import { formatPrice } from "@/Helpers/formatPrice";
import { priceBeforeTax } from "@/Helpers/priceBeforeTax";
import { symbolForCurrency } from "@/Helpers/symbolForCurrency";
import type { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import type { QuoteRestriction } from "@freightsimple/generated-dashboard-openapi-client/models/QuoteRestriction";
import type moment from "moment";
import type { ReactNode } from "react";
import QuotesRestrictionsWarningBubble from "../Bubbles/QuotesRestrictionsWarningBubble";

interface QuoteRowHeaderProps {
  showSecondaryInfo: boolean;
  carrierIdentifier: string;
  serviceDisplayName: string;
  price: number;
  taxAmount: number;
  currency: Currency;
  deliveryDate: moment.Moment | undefined;
  earliestTransitDays: number | undefined;
  latestTransitDays: number | undefined;
  notes?: string | undefined;
  priceBeforeCoupon?: number | undefined;
  transitBusinessDaysDescription?: string | undefined;
  restrictions?: QuoteRestriction[] | undefined;
}

const TransitDaysType = {
  Unknown: 0,
  SameDay: 1,
  Regular: 2,
} as const;
type TransitDaysType = (typeof TransitDaysType)[keyof typeof TransitDaysType];

// Small header label above each column ("Service Level", "Transit", …).
function HeaderLabel_({ children }: { children: ReactNode }) {
  return (
    <div className="text-[14px] font-light text-content-muted in-[.quote-card-medium]:text-[12px]">
      {children}
    </div>
  );
}

// The medium price block: a raised currency symbol, the big amount (its colour
// is inherited — the original's `color:"#545454"` was quoted and thus ignored),
// and a currency label dropped a touch below the baseline.
function MediumPrice_({
  currency,
  price,
}: {
  currency: string;
  price: number;
}) {
  return (
    <div className="flex flex-row items-center justify-end">
      <div className="relative -top-[3px] text-[14px] font-semibold text-foreground in-[.quote-card-medium]:text-[11px]">
        {symbolForCurrency(currency)}
      </div>
      <div className="text-[28px] font-semibold in-[.quote-card-medium]:text-[22px]">
        {formatPrice(price)}
      </div>
      <div className="relative top-[3px] ml-[2px] text-[20px] font-semibold text-content-muted in-[.quote-card-medium]:text-[15px]">
        {currency}
      </div>
    </div>
  );
}

function QuotesRowHeader(props: QuoteRowHeaderProps) {
  const serviceType = props.serviceDisplayName;

  const formattedDeliveryDate =
    props.deliveryDate?.format("ddd Do MMMM YYYY") || "Unknown";

  function formatTransitDays(): {
    transit: ReactNode;
    serviceType: ReactNode;
    type: TransitDaysType;
  } {
    if (props.earliestTransitDays === undefined) {
      return { transit: "Unknown", serviceType, type: TransitDaysType.Unknown };
    } else if (props.latestTransitDays === undefined) {
      return {
        transit: <>{props.earliestTransitDays}</>,
        serviceType,
        type: TransitDaysType.Regular,
      };
    } else {
      if (props.earliestTransitDays === props.latestTransitDays) {
        if (props.earliestTransitDays === 0) {
          // Keep the service name (e.g. Same Day / Rush / Direct) in the Service
          // Level slot; show the timing detail ("By 05:00 PM", "4 Hours") under
          // Transit instead of letting it hide the name. See ENG-6077.
          return {
            transit: <>{props.transitBusinessDaysDescription ?? "Same Day"}</>,
            serviceType,
            type: TransitDaysType.SameDay,
          };
        }
        return {
          transit: <>{props.earliestTransitDays}</>,
          serviceType,
          type: TransitDaysType.Regular,
        };
      }
      return {
        transit: (
          <>
            {props.earliestTransitDays}
            <T color="faint"> - </T>
            {props.latestTransitDays}
          </>
        ),
        serviceType,
        type: TransitDaysType.Regular,
      };
    }
  }

  const {
    transit: formattedTransitDays,
    serviceType: formattedServiceType,
    type: transitDaysType,
  } = formatTransitDays();

  const deliveryDateClass =
    formattedDeliveryDate === "Unknown"
      ? "text-content-faint"
      : "text-foreground";

  return (
    /* flex-1 is load-bearing: RevealBox lays the header out with
     * justify-between (content ... chevron), so a shrink-wrapping header
     * would size to its content and every row's columns would land at
     * different x positions. */
    <div className="flex min-h-24 min-w-0 flex-1 flex-row items-center">
      {/* Carrier logo column — gives most of its width back on narrow cards.
       * Every logo gets the same box, left-aligned in the column, with the
       * logo centred inside it so a narrow portrait mark (dayross is
       * 110x159) doesn't hug the left edge. */}
      <div className="flex w-[206px] flex-row items-center justify-start self-stretch in-[.quote-card-medium]:w-[150px] in-[.quote-card-narrow]:w-[110px]">
        <LogoBackdrop>
          <span className="flex h-12 w-[150px] items-center justify-center in-[.quote-card-medium]:w-[110px] in-[.quote-card-narrow]:w-[90px]">
            <img
              className="max-h-full max-w-full"
              alt={displayNameForCarrier(props.carrierIdentifier)}
              src={`/carrierLogos/${props.carrierIdentifier}.png`}
            />
          </span>
        </LogoBackdrop>
      </div>

      {/* Secondary info fades in once the row is collapsed. */}
      <div
        className={`flex-1 overflow-hidden transition-opacity duration-200 ${
          props.showSecondaryInfo ? "opacity-100" : "opacity-0"
        }`}
      >
        {/* One deliberate line on a wide card: fixed-ish columns with the
         * delivery block absorbing the slack (its date wraps inside the block
         * before anything else moves). Content never decides the layout — the
         * measured .quote-card-narrow class switches to a two-column grid
         * (service | price on top, mirroring the wide card), and
         * .quote-card-tiny collapses that to a single stacked column. */}
        <div className="flex flex-row items-center gap-x-6 py-2 in-[.quote-card-medium]:gap-x-4 in-[.quote-card-narrow]:grid in-[.quote-card-narrow]:grid-cols-2 in-[.quote-card-narrow]:gap-x-4 in-[.quote-card-narrow]:gap-y-3 in-[.quote-card-tiny]:grid-cols-1">
          {/* Service Level */}
          <div className="order-1 flex w-[170px] shrink-0 flex-col items-start in-[.quote-card-medium]:w-[140px] in-[.quote-card-narrow]:w-full">
            <HeaderLabel_>Service Level</HeaderLabel_>
            <div className="flex flex-row items-center gap-[2px]">
              <div className="text-[20px] font-semibold text-foreground in-[.quote-card-medium]:text-[17px]">
                {formattedServiceType}
              </div>
            </div>
            {props.notes &&
              !(props.restrictions && props.restrictions.length > 0) && (
                <Tooltip content={props.notes}>
                  <div className="max-w-[200px] overflow-hidden text-[11px] text-ellipsis whitespace-nowrap text-content-muted">
                    {props.notes}
                  </div>
                </Tooltip>
              )}
            {props.restrictions && props.restrictions.length > 0 && (
              <div className="flex flex-row gap-[4px] text-[12px]">
                <T color="muted">Restrictions Apply</T>
                <QuotesRestrictionsWarningBubble
                  restrictions={props.restrictions}
                />
              </div>
            )}
          </div>

          {/* Earliest Delivery — the flexible column: it gives ground first,
              wrapping its date onto a second line inside the block. */}
          <div className="order-2 flex min-w-[110px] flex-1 flex-col items-start in-[.quote-card-narrow]:order-3 in-[.quote-card-narrow]:w-full in-[.quote-card-narrow]:flex-none">
            <HeaderLabel_>Earliest Delivery </HeaderLabel_>
            <div
              className={`text-[18px] font-medium in-[.quote-card-medium]:text-[15px] ${deliveryDateClass}`}
            >
              {formattedDeliveryDate}
            </div>
          </div>

          {/* Transit */}
          <div className="order-3 flex w-[140px] shrink-0 flex-col items-start in-[.quote-card-medium]:w-[110px] in-[.quote-card-narrow]:order-4 in-[.quote-card-narrow]:w-full">
            <HeaderLabel_>Transit</HeaderLabel_>
            <div className="flex flex-row">
              <div className="text-[18px] font-medium text-foreground in-[.quote-card-medium]:text-[16px]">
                {formattedTransitDays}
              </div>
              {transitDaysType === TransitDaysType.Regular && (
                <div className="ml-[4px] flex flex-col items-start">
                  <div className="mt-[3px] text-[8px] text-content-muted">
                    Business
                  </div>
                  <div className="-mt-[5px] text-[8px] text-content-muted">
                    Days
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Price — a fixed-width column, so the Transit column and the
              price's right edge sit at the same x on every row no matter how
              many digits the price has. In the narrow grid it moves up next
              to Service Level (top-right, like the wide card); in the tiny
              single column it left-aligns with everything else. */}
          <div className="order-4 flex w-[220px] shrink-0 flex-col items-end in-[.quote-card-medium]:w-[170px] in-[.quote-card-narrow]:order-2 in-[.quote-card-narrow]:w-full in-[.quote-card-tiny]:items-start">
            {props.priceBeforeCoupon && (
              <div className="rounded-[2px] bg-gold px-[4px] py-[2px] text-right text-[11px] whitespace-nowrap text-on-bright">
                <b>${formatPrice(props.priceBeforeCoupon - props.price)}</b>{" "}
                coupon applied
              </div>
            )}
            <div className="h-1" />
            <MediumPrice_
              currency={props.currency}
              price={priceBeforeTax(props.price, props.taxAmount)}
            />
            {props.taxAmount > 0 && (
              <div className="-mt-[4px] text-right text-[14px] leading-none font-light whitespace-nowrap text-foreground in-[.quote-card-medium]:text-[12px]">
                After tax:{" "}
                <T weight="medium">
                  <span className="relative -top-[2px] text-[10px]">$</span>
                  {formatPrice(props.price)}
                </T>
                <span className="ml-[1px] text-[8px] font-semibold text-content-muted">
                  {props.currency}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default QuotesRowHeader;
